import React, { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CaseTabContext } from 'app/edozvola/cases/case-form-wrapper';
import { FormikProvider, useFormik } from 'formik';
import { Loader } from 'app/shared/loader/loader';
import { Button, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { PredictiveInput } from 'edozvola-frontend-lib/dist/ui/components/predictive-input';
import {
  initialPhasedConstructionModel,
  PhasedConstructionModel,
  PhasedConstructionType,
} from 'app/edozvola/cases/phased-construction/model/phased-construction.model';
import { phasedConstructionValidationSchema } from 'app/edozvola/cases/phased-construction/model/phased-construction.schema';
import {
  fetchPhasedConstructionQuery,
  savePhasedConstructionQuery,
} from 'app/edozvola/cases/phased-construction/service/phased-construction.queries';
import PhasedConstructionForm from 'app/edozvola/cases/phased-construction/phased-construction/phased-construction-form';
import StagedConstructionForm from 'app/edozvola/cases/phased-construction/staged-construction/staged-construction-form';
import { fetchPhasedConstructionTypeOptionsQuery } from 'app/edozvola/codebooks/codebooks.queries';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";
import { getCaseContext } from 'app/shared/util/context-util';

const PhasedConstructionMain = () => {
  const { id: uuid } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { sidebarItemPair } = getCaseContext();
  const [showTypeChangeModal, setShowTypeChangeModal] = useState(false);
  const [pendingTypeSelection, setPendingTypeSelection] = useState<any>(null);

  const { data: phaseConstructionData, isLoading: phaseConstructionDataLoading } = fetchPhasedConstructionQuery(uuid);
  const { data: phasedConstructionTypeOptions, isLoading: phasedConstructionTypeOptionsLoading } =
    fetchPhasedConstructionTypeOptionsQuery();

  const phasedConstructionTypeOptionsData = phasedConstructionTypeOptions || [];

  const isInitialDataEmpty =
    !phaseConstructionData?.phasedConstructionType?.length &&
    (phaseConstructionData?.stages?.length ?? 0) === 0 &&
    (phaseConstructionData?.phases?.length ?? 0) === 0;

  const defaultPhasedOption = phasedConstructionTypeOptionsData.find(option => option.id === PhasedConstructionType.PHASED);

  const shouldSetDefaultPhased = isInitialDataEmpty && !sidebarItemPair?.readOnly && defaultPhasedOption;

  const initialValues = {
    ...initialPhasedConstructionModel,
    ...phaseConstructionData,
    ...(shouldSetDefaultPhased
      ? {
        phasedConstructionType: [defaultPhasedOption],
        ...(defaultPhasedOption.id === PhasedConstructionType.PHASED
          ? {
            phases: [{ phaseNumber: 1, phaseValue: '' }],
          }
          : {}),
      }
      : {}),
  };

  const formik = useFormik({
    initialValues,
    validationSchema: phasedConstructionValidationSchema,
    onSubmit: (values: PhasedConstructionModel) => {
      savePhasedConstructionData(values);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { mutate: savePhasedConstructionData, isPending: isPhasedConstructionSaving } = savePhasedConstructionQuery(uuid, () => {});

  const selectedTypeId = formik.values.phasedConstructionType?.[0]?.id;

  const onTypeChange = (selected: any) => {
    const newSelectedTypeId = selected?.[0]?.id;
    const oldSelectedTypeId = formik.values.phasedConstructionType?.[0]?.id;

    const isDataSaved = phaseConstructionData?.stages.length > 0 || phaseConstructionData?.phases.length > 0;

    if (!newSelectedTypeId && oldSelectedTypeId) {
      formik.setFieldValue('phasedConstructionType', selected);
      setPhasedOrStaged(oldSelectedTypeId as number);
      return;
    }

    if (oldSelectedTypeId === newSelectedTypeId) {
      return;
    }

    if (isDataSaved && newSelectedTypeId && oldSelectedTypeId !== newSelectedTypeId) {
      setPendingTypeSelection(selected);
      setShowTypeChangeModal(true);
    } else {
      formik.setFieldValue('phasedConstructionType', selected);
      setPhasedOrStaged(newSelectedTypeId);
    }
  };

  const applyPendingTypeChange = () => {
    if (!pendingTypeSelection) {
      return;
    }

    formik.setFieldValue('phasedConstructionType', pendingTypeSelection);
    setPhasedOrStaged(pendingTypeSelection?.[0]?.id);

    setShowTypeChangeModal(false);
    setPendingTypeSelection(null);
  };

  const setPhasedOrStaged = (typeId: number) => {
    if (typeId === PhasedConstructionType.PHASED) {
      formik.setFieldValue('stages', []);
      formik.setFieldValue('phases', [{ phaseNumber: 1, phaseValue: '' }]);
    } else if (typeId === PhasedConstructionType.STAGED) {
      formik.setFieldValue('phases', []);
      formik.setFieldValue('stages', [
        {
          stageNumber: 1,
          stageValue: '',
          phases: [],
        },
      ]);
    }

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  return (
    <FormikProvider value={formik}>
      <FormikValueObserver
        onChange={dirty => {
          if (dirty.dirty) {
            dispatch(setFormChanged(true));
          } else {
            dispatch(initCaseFormChange());
          }
        }}
      />
      <div className={'pb-4'}>
        <Row>
          <Col md={5}>
            <Label labelText={'Građenje'} />
            <PaginatedDropdown
              placeholder={!sidebarItemPair?.readOnly && 'Odaberi građenje'}
              onItemSelect={selected => onTypeChange(selected)}
              selected={formik.values.phasedConstructionType || []}
              labelKey={'value'}
              options={phasedConstructionTypeOptions || []}
              disabled={sidebarItemPair?.readOnly}
              error={!sidebarItemPair?.readOnly && (formik.errors.phasedConstructionType as string)}
            />
          </Col>
        </Row>
      </div>

      <div>
        {phaseConstructionDataLoading && <Loader />}
        {!phaseConstructionDataLoading && (
          <>
            {selectedTypeId === PhasedConstructionType.PHASED && (
              <PhasedConstructionForm setFieldValue={formik.setFieldValue} readOnly={sidebarItemPair?.readOnly} formik={formik} />
            )}
            {selectedTypeId === PhasedConstructionType.STAGED && (
              <StagedConstructionForm setFieldValue={formik.setFieldValue} readOnly={sidebarItemPair?.readOnly} formik={formik} />
            )}
            {!sidebarItemPair?.readOnly && (
              <div className={'case-save-button'}>
                <Button
                  label={'Spremi'}
                  size={'lg'}
                  disabled={!formik.dirty || !formik.isValid}
                  isLoading={isPhasedConstructionSaving}
                  color={'info'}
                  onClick={() => formik.handleSubmit()}
                />
              </div>
            )}

            <Modal scrollable isOpen={showTypeChangeModal} toggle={() => setShowTypeChangeModal(false)} centered>
              <ModalHeader>Promjena načina odvijanja građenja nakon što su podaci na kartici spremljeni</ModalHeader>
              <ModalBody>Promjenom načina odvijanja građenja prethodno uneseni podaci će se obrisati! Želite li nastaviti?</ModalBody>
              <ModalFooter>
                <Button
                  label={'Odustani'}
                  size={'lg'}
                  onClick={() => {
                    setShowTypeChangeModal(false);
                    setPendingTypeSelection(null);
                  }}
                />
                <Button label={'Nastavi'} size={'lg'} color={'info'} onClick={applyPendingTypeChange} />
              </ModalFooter>
            </Modal>
          </>
        )}
      </div>
    </FormikProvider>
  );
};

export default PhasedConstructionMain;
